import { Component, OnInit, Output, EventEmitter } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { BikeService } from "../../services/bike.service";
import { Bike } from "../../models/bike";
import { Lock } from "../../models/lock";

import { DEFAULT_LAT, DEFAULT_LONG } from "../../constants";
import * as L from "leaflet";
import "leaflet.markercluster";
import { Group } from "../../models/group";
import { GroupService } from "../../services/group.service";
import { Geopoint } from "../../models/geopoint";
import { ToastsManager } from "ng2-toastr";
import { LockService } from "../../services/lock.service";
import { TranslateService } from "@ngx-translate/core";
import { AttachmentPoint } from "../../models/attachmentpoint";
import { StationItem } from "../../models/station";
import { AttachmentPointService } from "../../services/attachment-point.service";
import { environment } from "../../../environments/environment";
declare var $: any;
@Component({
  selector: "app-bikes-form",
  templateUrl: "./bikes-form.component.html",
  styleUrls: ["./bikes-form.component.css"]
})
export class BikesFormComponent implements OnInit {
  public bike: Bike;
  bikeGroupName: string;
  groupId: string;
  lockId: any = "no-lock";
  originalLockId;
  originalLockType;

  selectedType;

  /** Indicates whether we are in creation or update mode */
  creationMode: boolean;
  loadHistory = false;

  @Output() onUpdateValidation = new EventEmitter<Bike>();

  /* ============ Leaflet configuration ============ */
  /** leaflet options */
  options = {
    layers: [
      L.tileLayer(environment.osmUrl, {
        maxZoom: 18,
        attribution: "Open Street Map"
      })
    ],
    zoom: 14,
    center: L.latLng(DEFAULT_LAT, DEFAULT_LONG)
  };

  /* binded map properties */
  center: L.LatLng = this.options.center;
  markers: any[] = [];
  markerClusterGroup: any;
  markerClusterOptions: L.MarkerClusterGroupOptions;
  zoom: number = this.options.zoom;
  fitBounds: any;

  station: StationItem;

  /** Non bind to station attachment points from selected group (no subgrp) */
  apLightFree: AttachmentPoint[];

  /** Selectale fields */
  visibleGroups: Group[] = [];
  visibleLocks: Lock[] = [];
  bikeCurrentAp: AttachmentPoint;
  bikeCurrentLock: Lock;

  constructor(
    public activeModal: NgbActiveModal,
    private apService: AttachmentPointService,

    public bikeService: BikeService,
    public lockService: LockService,
    public groupService: GroupService,
    public toastManager: ToastsManager,
    public translate: TranslateService
  ) {}

  ngOnInit() {
    if (this.creationMode) {
      this.bike = new Bike();
      this.bike.state = "AVAILABLE"; // default value
    } else {
      this.addMarker();
      // loads available AP for this group
      this.apService
        .get({ id: this.bike.attachmentPointId } as AttachmentPoint)
        .subscribe(res => (this.bikeCurrentAp = res));
      this.apService.getFreeAPByGroup(this.bike.groupId).subscribe(res => {
        this.apLightFree = res.res;
      });
    }

    // loading groups to display
    this.groupService.loadVisibleGroups().subscribe(res => {
      this.visibleGroups = res;
      if (!this.creationMode) {
        this.groupId = String(this.bike.groupId);

        if (this.bike.lockId) {
          this.lockId = this.bike.lockId;
          this.originalLockId = this.lockId;
          this.originalLockType = this.selectedType = this.bike.lockType;

          // loading locks to display
          this.lockService
          .get({ id: this.bike.lockId } as Lock)
            .subscribe(res => (this.bikeCurrentLock = res));
          this.lockService
            .getFreeLockByGroupAndType(this.bike.groupId, this.bike.lockType)
            .subscribe(res => (this.visibleLocks = res.res));
        }
      }
    });
  }

  lockTypeChange(value: string, groupId?: number) {
    if (value === "MECHANICAL" || value === "ELECTRICAL") {
      // get lock by selected group and not bike group id
      this.selectedType = value;
      this.bike.lockType = value;
      this.lockService
        .getFreeLockByGroupAndType(groupId ? groupId : this.bike.groupId, value)
        .subscribe(res => (this.visibleLocks = res.res));
    } else {
      this.bike.lockType = undefined;
    }
    this.lockId = "no-lock";
    this.bike.lockId = undefined;
    this.changeLock();
  }

  addMarker() {
    const newMarker = L.marker(
      [this.bike.location.latitude, this.bike.location.longitude],
      {
        icon: L.icon({
          iconSize: [41, 51],
          iconAnchor: [20, 51],
          popupAnchor: [0, -51],
          iconUrl: "/assets/marker_100.png"
        })
      }
    );
    this.markers.push(newMarker);
    this.center = L.latLng(
      this.bike.location.latitude,
      this.bike.location.longitude
    );
  }

  markerClusterReady(group: L.MarkerClusterGroup) {
    this.markerClusterGroup = group;
  }

  changeStateTo(state: string) {
    this.bike.state = state;
  }

  changeGroup() {
    this.bike.groupId = parseInt(this.groupId, 10);
    const selectedGroup = this.visibleGroups.find(
      g => g.id === parseInt(this.groupId, 10)
    );
    this.bike.groupName = this.visibleGroups.find(
      g => g.id === this.bike.groupId
    ).name;
    this.lockTypeChange(this.lockId, parseInt(this.groupId, 10));
    this.apService.getFreeAPByGroup(this.bike.groupId).subscribe(res => {
      this.apLightFree = res.res;
    });
  }

  changeLock() {
    if (this.lockId === "no-lock") {
      this.bike.lockId = undefined;
    } else {
      this.bike.lockId = this.lockId;
    }
  }

  onSaveClick() {
    if (!this.bike.attachmentPointId) {
      delete this.bike.attachmentPointId;
    }
    if (this.creationMode) {
      this.bike.location = new Geopoint(); // non initialised location
      this.bikeService.create(this.bike).subscribe(res => {
        this.toastManager.success(
          this.translate.instant("toast.bikecreated"),
          this.translate.instant("toast.success")
        );
        this.onUpdateValidation.emit(res as Bike);
        this.activeModal.close();
      });
    } else {
      this.bikeService.update(this.bike).subscribe(res => {
        this.toastManager.success(
          this.translate.instant("toast.bikeupdated"),
          this.translate.instant("toast.success")
        );
        const resultBike = res as Bike;
        resultBike.stationName = this.bike.stationName;
        resultBike.groupName = this.visibleGroups.find(
          g => g.id === this.bike.groupId
        ).name;
        resultBike.attachmentPointId = this.bike.attachmentPointId;
        this.onUpdateValidation.emit(resultBike);
        this.activeModal.close();
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/bikes/bikes-form/bikes-form.component.ts